import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { interval, merge, Observable, of } from 'rxjs';
import { catchError, exhaustMap, map, startWith, switchMap } from 'rxjs/operators';
import { LocalStorage } from '../local-storage/local-storage';
import { ISynchronisationService } from '../synchronisation/iSynchronisation-service';
import { UserService } from '../auth/user.service';
import { NetworkProvider } from '../network/network';
import { ToastController } from '@ionic/angular';
import { AgendaHelperService } from './agenda.helper.service';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { ByDayCollection } from '../../models/collections.model';
import { AgendaJourTempo } from '../../models/agenda/agenda-jour.model';
import { environment } from '../../../../environments/environment';
import { getOnOffConfigOrElse } from '../config/config.service';

@Injectable({ providedIn: 'root' })
export class AgendaTempoService extends LocalStorage implements ISynchronisationService {
  private dateDebut: moment.Moment;
  private dateFin: moment.Moment;

  constructor(
    private userService: UserService,
    private http: HttpClient,
    protected override storage: Storage,
    protected override network: NetworkProvider,
    private agendaHelperService: AgendaHelperService,
    private toastCtrl: ToastController,
    private translate: TranslateService
  ) {
    super();
    this.serverRequestPeriod = this.agendaHelperService.AGENDA_REFRESH_DELAY;
  }

  /**
   * Synchronisation forcé du service.
   * @returns Observable retournant le nombre d'entitées disponible en local après la synchronisation.
   */
  synchronisation(): Observable<number> {
    return this.getAgendaTempoServer(moment().toDate()).pipe(
      switchMap(() => {
        return fromPromise(this.storage.get(this.AGENDA_TEMPO_KEY)).pipe(
          switchMap((agenda) => {
            const localData = new ByDayCollection<AgendaJourTempo>(agenda);
            let count = 0;

            for (const day of localData.getValues()) {
              for (const list in day) {
                count += day[list].length;
              }
            }

            return of(count);
          }),
          catchError(() => of(0))
        );
      })
    );
  }

  /**
   * Retourne les jours contenant les données de tempo autour de la date, soit depuis le local storage,
   * soit depuis le serveur
   * @param displayedDate Date courante
   * @param forceRefresh
   */
  getAgendaTempo(displayedDate: Date, forceRefresh = false): Observable<ByDayCollection<AgendaJourTempo>> {
    return interval(this.serverRequestPeriod).pipe(
      map((iteration) => !!iteration),
      startWith(forceRefresh),
      switchMap((forceServerRequest) => {
        return this.shouldUseWebservice(this.AGENDA_TEMPO_KEY, forceServerRequest).pipe(
          exhaustMap((shouldUseWebservice) => {
            if (forceServerRequest && this.network.isConnected()) {
              return this.getAgendaTempoServer(moment().toDate());
            } else if (shouldUseWebservice && this.network.isConnected()) {
              return this.getAgendaTempoServer(displayedDate);
            } else {
              return fromPromise(this.storage.get(this.AGENDA_TEMPO_KEY)).pipe(
                exhaustMap((agendaData: { [id: string]: AgendaJourTempo }) => {
                  const agenda = new ByDayCollection(agendaData);
                  const displayedDateFormat = moment(displayedDate).format('YYYY-MM-DD');
                  if (agenda.has(displayedDateFormat) || !this.network.isConnected()) {
                    return of(agenda);
                  } else {
                    return this.getAgendaTempoServer(displayedDate);
                  }
                })
              );
            }
          })
        );
      }),
      catchError(() => {
        if (this.network.isConnected()) {
          this.presentToast(this.translate.instant('AGENDA.TEMPO.TOAST.ERROR')).then();
        }
        return of(new ByDayCollection<AgendaJourTempo>());
      })
    );
  }

  /**
   * Récupère les données tempo de l'agenda sur le serveur sur une periode de 3 mois autour de la date passée en paramètre
   * @param displayedDate Date courante
   */
  private getAgendaTempoServer(displayedDate: Date): Observable<ByDayCollection<AgendaJourTempo>> {
    const days = this.network.isConnected() ? 45 : 7;
    this.dateDebut = moment(displayedDate).subtract(days, 'day');
    this.dateFin = moment(displayedDate).add(days, 'day');

    return this.getAgendaTempoServerV2(this.dateDebut.toISOString(), this.dateFin.toISOString());
  }

  private getAgendaTempoServerV2(
    dateDebutISOString: string,
    dateFinISOString: string
  ): Observable<ByDayCollection<AgendaJourTempo>> {
    let url = `${environment.apis.API_DACTECH_BACK_V1}/agenda/${this.userService.getDkCodeRessource()}/tempo?dateDebut=${dateDebutISOString}&dateFin=${dateFinISOString}`;
    return merge(
      this.getTempoLocalStorage(),
      this.http.get<ByDayCollection<AgendaJourTempo>>(url).pipe(
        switchMap((res) => {
          const resultCollection = new ByDayCollection<AgendaJourTempo>(res);
          this.agendaHelperService.setCollectionToLocal(
            resultCollection,
            this.AGENDA_TEMPO_KEY,
            this.dateDebut,
            this.dateFin
          );
          return of(resultCollection);
        })
      )
    );
  }

  private getTempoLocalStorage(): Observable<ByDayCollection<AgendaJourTempo>> {
    return fromPromise(this.storage.get(this.AGENDA_TEMPO_KEY)).pipe(
      exhaustMap((agendaData: { [id: string]: AgendaJourTempo }) => {
        return of(new ByDayCollection(agendaData));
      })
    );
  }

  private async presentToast(message: string) {
    let toast = await this.toastCtrl.create({
      message: message,
      duration: 5000,
    });
    await toast.present();
  }
}
